import { InternalAxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { SESSION_STORAGE_KEYS } from '../../consts/sessionStorage';
import { getOIDCToken, oidcSignout } from '../../utils/authentication';
import { saveAuthTokensToCookie } from '../../utils/cookies';
import { isAirgappedMode } from '../../utils/env';
import {
  refreshAccessToken,
  getAccessToken,
  getRefreshToken,
} from '../../utils/token';
import {
  appendCliRedirectParams,
  isAuthenticatedPage,
  isCrossOriginUrl,
  isSuccessSigninExternalClientPage,
} from '../../utils/url';
import { getServerUrl, resolveAndValidateRedirect } from './utils';

/**
 * Marks a rejection as "a 409 tenant redirect is already navigating", not a real request
 * failure. Callers use it to hold rendering until the navigation lands rather than reacting to
 * the error.
 */
export const REDIRECT_IN_PROGRESS_ERROR_NAME = 'RedirectInProgressError';

/**
 * Injects common interceptors into the provided axios instance.
 * @param axiosInstance - The axios instance to modify
 * @param options - Optional flags to control which interceptors are added
 */
export function injectCommonInterceptors(
  axiosInstance: AxiosInstance,
  options?: {
    sharedHeaders?: boolean;
    authorization?: boolean;
    conflictRedirect?: boolean;
    refreshToken?: boolean;
    dynamicBaseUrl?: boolean;
  },
) {
  const {
    sharedHeaders = true,
    authorization = true,
    conflictRedirect = true,
    refreshToken = true,
    dynamicBaseUrl = true,
  } = options || {};

  if (dynamicBaseUrl) {
    axiosInstance.interceptors.request.use(reanchorBaseUrlToServerUrl);
  }

  if (sharedHeaders) {
    axiosInstance.interceptors.request.use(addSharedHeaders);
  }

  if (authorization) {
    axiosInstance.interceptors.request.use(addAuthorizationHeader);
  }

  if (refreshToken) {
    axiosInstance.interceptors.response.use(
      (response) => response,
      createRefreshTokenErrorInterceptor(axiosInstance),
    );
  }

  if (conflictRedirect) {
    axiosInstance.interceptors.response.use(
      (response) => response,
      create409RedirectInterceptor(),
    );
  }
}

/**
 * Re-anchors a request's baseURL onto the currently-resolved server_url.
 *
 * Client baseURLs are computed at module-eval time (before the async config load),
 * so on deployments that resolve config from the backend the frozen origin can be
 * stale. Every client baseURL is server_url-anchored (the Gitlab/OIDC bases derive
 * from server_url / auth_url_oidc, which equals server_url), so swapping the origin
 * for the resolved one at request time yields the correct URL and removes the
 * module-eval freeze. See DEPLOYMENT_PLAN.md.
 * @param config - The request config
 * @returns The request config with its baseURL re-anchored on the live server_url
 */
export function reanchorBaseUrlToServerUrl(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.baseURL) return config;

  try {
    const { pathname, search } = new URL(config.baseURL);
    const serverUrl = getServerUrl().replace(/\/+$/, '');

    config.baseURL = `${serverUrl}${pathname}${search}`;
  } catch {
    // Non-absolute baseURL (shouldn't happen for these clients) — leave as-is.
  }

  return config;
}

/**
 * Adds shared headers to all requests (e.g., Request-id for tracking)
 * @param config - The request config
 * @returns The request config with shared headers
 */
export function addSharedHeaders(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  config.headers['Request-id'] = uuidv4();
  config.headers['Qodo-Client-Type'] = 'portal';

  return config;
}

/**
 * Adds the Authorization header to the request.
 * @param config - The request config
 * @returns The request config with the Authorization header
 */
export async function addAuthorizationHeader(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const getToken = getOIDCToken;

  const token = await getToken();

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
}

/**
 * Clears all authentication tokens from localStorage.
 * Called after successfully saving tokens to cookie for cross-subdomain redirect.
 */
const clearAuthTokensFromLocalStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);

  console.info('Login: Cleared data');
};

/**
 * Moves the access token and refresh token from localStorage to cookies for cross-subdomain redirects.
 * This ensures the tokens are available on the destination subdomain even if localStorage is not shared.
 * Clears the tokens from localStorage after successfully saving to cookies.
 * Only sets the cross-domain redirect guard when the destination is a different origin,
 * so same-origin 409 redirects are unaffected.
 *
 * On every failure path localStorage is left intact, so the caller can safely abandon the
 * redirect and leave the user with a working session at this origin.
 * @param redirectTo - The destination URL from the 409 response
 * @returns true if the tokens now live in the shared cookie
 */
const moveAuthTokensToCookie = (redirectTo: string): boolean => {
  // Tracked outside the try: once the cookie holds the credential the destination portal can
  // complete the login, so a throw in a later step must not report the handoff as failed.
  let cookieSaved = false;

  try {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    const tokenExpiry = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY);
    const idToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ID_TOKEN);

    if (!accessToken || !refreshToken) {
      console.warn(
        'No access token found to move to cookie before 409 redirect',
      );

      return false;
    }

    cookieSaved = saveAuthTokensToCookie(
      accessToken,
      refreshToken,
      tokenExpiry || undefined,
      idToken || undefined,
    );

    if (!cookieSaved) {
      console.warn(
        'Failed to move auth tokens to cookie before redirect. ' +
          'Tokens may not be available on destination subdomain.',
      );

      return false;
    }

    clearAuthTokensFromLocalStorage();

    if (isCrossOriginUrl(redirectTo)) {
      // Guard: prevent in-flight retry requests at this origin from consuming the migration
      // cookie before the browser navigates away. Stored as a timestamp so it self-expires
      // after 30 s (handles aborted navigations). Invisible to the destination portal —
      // different origin means separate localStorage.
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CROSS_DOMAIN_REDIRECT_IN_PROGRESS,
        Date.now().toString(),
      );
    }

    return true;
  } catch (cookieError) {
    console.error(
      'Error moving tokens to cookie before redirect:',
      cookieError,
    );

    // Report on whether the credential reached the cookie, not on whether every step ran.
    // A throw after the save (clearing localStorage, writing the guard) still leaves the
    // destination able to authenticate — cancelling the hop there would strand the user on
    // this origin with its localStorage tokens already removed.
    return cookieSaved;
  }
};

/**
 * Creates a response interceptor that handles HTTP 409 redirects.
 * When a 409 response contains a redirectTo URL, performs a full page redirect.
 * Includes safeguards against redirect loops and invalid URLs.
 * @returns A response error interceptor function
 */
export function create409RedirectInterceptor(): (
  error: AxiosError,
) => Promise<never> {
  return async (error: AxiosError): Promise<never> => {
    const isConflictError = error.response?.status === StatusCodes.CONFLICT;

    // Only handle 409 errors
    if (!isConflictError) {
      return Promise.reject(error);
    }

    // Resolve and validate redirect URL
    const validationResult = resolveAndValidateRedirect(error);

    if (!validationResult.valid) {
      console.warn(validationResult.reason);
      console.warn('Cannot perform redirect without valid URL');

      return Promise.reject(error);
    }

    // Guard against same-origin redirect loops: only redirect from an in-app page (or the
    // CLI device-login success page, which a dedicated-org CLI login must redirect MT→ST
    // from). A cross-origin redirect is a tenant bounce (e.g. the sign-in page telling the
    // user to sign in on the central portal), not a loop, so allow it from anywhere —
    // including the sign-in page — since the target host is a different origin.
    const isTenantBounce = isCrossOriginUrl(validationResult.redirectTo);
    const canRedirectFromHere =
      isTenantBounce ||
      isAuthenticatedPage() ||
      isSuccessSigninExternalClientPage();

    if (!canRedirectFromHere) {
      console.log('Not an authenticated page, passing through');

      return Promise.reject(error);
    }

    // Carry the CLI device-login context (user_code/trace_id) onto the ST URL so the
    // ST landing leg can adopt the code + auto-confirm. No-op for non-CLI logins, and
    // origin-preserving so the cross-origin token-handoff guard below is unaffected.
    const redirectTo = appendCliRedirectParams(validationResult.redirectTo);

    // Track this redirect in sessionStorage (best-effort — loop-detection telemetry only).
    // Guarded because this interceptor now also runs on the external-client success page,
    // where storage may be blocked/disabled; a throw here must not abort the redirect.
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.LAST_REDIRECT,
        JSON.stringify({ url: redirectTo, timestamp: Date.now() }),
      );
    } catch {
      // storage unavailable — skip loop tracking, still perform the redirect
    }

    const handoffSucceeded = moveAuthTokensToCookie(redirectTo);

    // A tenant bounce with no credential to carry is a guaranteed loop: the destination has no
    // token, rejects, and 409s straight back here. Stay put instead — localStorage is untouched
    // on every handoff failure path, so the session at this origin still works. Reachable when
    // the two portals share no cookieable parent domain (see getSharedDomain).
    if (isTenantBounce && !handoffSucceeded) {
      console.error(
        'Aborting cross-origin 409 redirect: auth token handoff failed',
      );

      return Promise.reject(error);
    }

    // Perform redirect
    console.info('409 redirect to:', redirectTo);
    window.location.href = redirectTo;

    // Return a rejected promise to allow proper cleanup and garbage collection
    // The redirect will happen regardless, but rejecting allows the promise chain to clean up
    const redirectError = new Error('Redirecting due to 409 conflict');

    redirectError.name = REDIRECT_IN_PROGRESS_ERROR_NAME;

    return Promise.reject(redirectError);
  };
}

/**
 * Creates a response interceptor that handles token refresh for unauthorized requests
 * @param axiosInstance - The axios instance to use for retrying requests
 * @returns A response interceptor function
 */
export function createRefreshTokenErrorInterceptor(
  axiosInstance: AxiosInstance,
) {
  return async (error: any) => {
    if (isAirgappedMode()) {
      return Promise.reject(error);
    }

    // If we are already on the signin page or error page, don't do anything. Some meta http requests might be sent when we are on the signin page.
    if (!isAuthenticatedPage()) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const isUnauthorizedError =
      error.response?.status === StatusCodes.UNAUTHORIZED;
    const isForbiddenError = error.response?.status === StatusCodes.FORBIDDEN;

    // Handle 403 Forbidden errors - these are permission issues, not auth issues
    // Don't sign out the user, just return the error to be handled by the calling code
    if (isForbiddenError) {
      return Promise.reject(error);
    }

    if (isUnauthorizedError) {
      // Check if this is an Azure integration-specific error
      // These errors are about Azure DevOps OAuth tokens, not Qodo authentication
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage = error.response?.data?.message || '';
      const requestUrl = originalRequest?.url || '';

      const isAzureIntegrationError =
        errorDetail.includes('Azure') ||
        errorDetail.includes('reconnect') ||
        errorMessage.includes('Azure') ||
        errorMessage.includes('reconnect') ||
        requestUrl.includes('azure/');

      // GitLab integration 401s are upstream GitLab auth failures, not Qodo auth failures.
      // The GitLab client baseURL contains '/gitlab' while its request paths are relative (e.g. 'groups').
      const requestBaseUrl = originalRequest?.baseURL || '';
      const isGitlabIntegrationError =
        requestUrl.includes('gitlab/') || requestBaseUrl.includes('gitlab');

      // These are integration errors, not auth errors - don't sign out the user, just pass the error to be handled by the caller
      if (isAzureIntegrationError) {
        console.warn('Azure DevOps integration auth error - not signing out:', {
          errorDetail,
          errorMessage,
          requestUrl,
        });

        return Promise.reject(error);
      }

      if (isGitlabIntegrationError) {
        console.warn('GitLab integration auth error - not signing out:', {
          errorDetail,
          errorMessage,
          requestUrl,
          requestBaseUrl,
        });

        return Promise.reject(error);
      }

      // Check if this request has already been retried to prevent infinite loops
      const retryCount = originalRequest._retryCount || 0;
      const maxRetries = 1; // Only retry once to avoid infinite loops

      if (retryCount >= maxRetries) {
        console.warn('Max retry attempts reached for unauthorized request');
        oidcSignout();

        return Promise.reject(error);
      }

      try {
        const newTokens = await refreshAccessToken();

        if (newTokens) {
          originalRequest.headers['Authorization'] =
            `Bearer ${newTokens.access_token}`;
          originalRequest._retryCount = retryCount + 1;

          return axiosInstance(originalRequest);
        } else {
          oidcSignout();

          return Promise.reject(error);
        }
      } catch (error) {
        console.error('Token refresh failed');
        oidcSignout();

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  };
}
